<template>
    <section id="monitor-datatable" 
        class="monitor-datatable"
    >
        <template v-if="table_data_structure.length">
            <div
                v-if="show_copyright_logo"
                class="capture"
            >
                <div class="capture__text-container">
                    <v-progress-circular
                        class="capture__progress"
                        indeterminate
                        size="18"
                        width="2"
                        color="black"
                    />
                    &nbsp;&nbsp;&nbsp;  {{ $t('__datatable.capture_text') }}
                </div>
            </div>
            <div 
                v-for="({headers, data}, index) in table_data_structure" 
                :key="index" 
                class="table__content" 
                :style="css_props"
            >
                <v-data-table
                    class="bg-opacity-white elevation-1"
                    :headers="headers"
                    :hide-default-header="!show_header || custom_header"
                    :header-props="{
                        'sort-icon': 'icon-triangle'
                    }"
                    :items="data"
                    :search="search"
                    :no-data-text="$t('__datatable.no_data_text')"
                    :no-results-text="$t('__datatable.no_results_text')"
                    :items-per-page.sync="items_per_page"
                    :hide-default-footer="show_copyright_logo"
                    :custom-sort="custom_sort"
                    :mobile-breakpoint="0"
                    :footer-props="footer_props"
                    @current-items="change_width()"
                >
                    <template #footer.page-text="props">
                        <template v-if="items_per_page < 0">
                            {{ '1 / 1' }}    
                        </template>
                        <template v-else>
                            {{ Math.ceil(props.pageStop/items_per_page) }} / {{ Math.ceil(props.itemsLength/items_per_page) }}
                        </template>
                    </template>
                    <template v-if="custom_header" 
                        #header="{ props: { headers } }"
                    >
                        <thead>
                            <tr>
                                <template v-for="(header, key) in headers">
                                    <th v-if="!header.second_text && !header.third_text" 
                                        :key="`${key}_th1`"
                                        rowspan="3" 
                                        class="text_align_center"
                                    >
                                        {{ header.text }}
                                    </th>
                                    <th v-else-if="header.text" 
                                        :key="`${key}_th1`"
                                        :colspan="headers.filter(header => header.second_text || header.third_text).length" 
                                        class="text_align_center"
                                    >
                                        {{ header.text }}
                                    </th>
                                </template>
                            </tr>
                            <tr>
                                <template v-for="(header, key) in headers">
                                    <th v-if="header.second_text" 
                                        :key="`${key}_th2`"
                                        :colspan="headers.filter(header => header.third_text).length / headers.filter(header => header.second_text).length" 
                                        class="text_align_center"
                                    >
                                        {{ header.second_text }}
                                    </th>
                                </template>
                            </tr>
                            <tr>
                                <template v-for="(header, key) in headers">
                                    <th v-if="header.third_text" 
                                        :key="`${key}_th3`"
                                        class="text_align_center"
                                    >
                                        {{ header.third_text }}
                                    </th>
                                </template>
                            </tr>
                        </thead>
                    </template>
                    <template v-if="error_text" 
                        #body="{ headers }"
                    >
                        <tbody>
                            <tr>
                                <td :colspan="headers.length">
                                    <v-alert
                                        :value="true"
                                        type="error"
                                        :style="{height: 50 + 'px'}"
                                    >
                                        {{ error_text }}
                                    </v-alert>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                    <template v-else 
                        #item="{ item }"
                    >
                        <tr :id="`row_${item['項次'] || item['編號'] || 'unavailable'}`" 
                            :class="[
                                { 'tr--focal': (item['項次'] || item['編號']) === focal_sign_number },
                                table_label_function(item)
                            ]"
                        >
                            <td v-for="(value,key) in headers"
                                :key="key"
                                :class="[
                                    { 
                                        border_right: show_table_border,
                                        'td--focal': (item['項次'] || item['編號']) === focal_sign_number,
                                        'text_align_center': is_style_align_center(item[value.value], value.value)
                                    },
                                    (item['項次'] || item['編號']) === focal_sign_number && get_signal_focal_class(item, value)
                                ]"
                            >  
                                <v-tooltip 
                                    v-if="should_draw_diagram_header.includes(value.value) &&
                                        should_display_info_header.includes(value.value) &&
                                        generate_tooltop_display_info(item, value.value)"
                                    right
                                >
                                    <template #activator="{ on }">
                                        <span
                                            class="links tooltip__box"
                                            @click="show_dialog(value.value, item[value.value], item)"
                                            v-on="on"
                                        >
                                            {{ item[value.value] }}
                                        </span>
                                    </template>
                                    <span v-html="generate_tooltop_display_info(item, value.value)" />
                                </v-tooltip>

                                <!-- 為了將THB全流域雨量估計表上的link拿掉，所以加入 name != 8 此條件-->
                                <span 
                                    v-else-if="should_draw_diagram_header.includes(value.value) && name != 8" 
                                    class="links"
                                    @click="show_dialog(value.value, item[value.value], item)"
                                >
                                    {{ item[value.value] }}
                                </span>
                                <v-tooltip 
                                    v-else-if="should_display_info_header.includes(value.value) && 
                                        generate_tooltop_display_info(item, value.value)"
                                    right
                                >
                                    <template #activator="{ on }">
                                        <span 
                                            v-on="on"
                                            class="tooltip__box"
                                            v-html="item[value.value]"
                                        />
                                    </template>
                                    <span v-html="generate_tooltop_display_info(item, value.value)" />
                                </v-tooltip>
                                <template v-else-if="should_show_raw_html_header.includes(value.value)">
                                    <!-- 警示圖案 -->
                                    <template v-for="(icon, icon_key) in item[value.value]">
                                        <v-tooltip v-if="icon.tooltip_content" 
                                            :key="icon_key"
                                            right
                                        >
                                            <template #activator="{ on }">
                                                <span
                                                    v-on="on"
                                                    class="tooltip__box"
                                                    :class="{
                                                        'table__alert--map-active': is_monitor_open_emap
                                                    }"
                                                    @click="focus_on_sign(item, value)"
                                                    v-html="icon.html"
                                                />
                                            </template>
                                            <span v-html="icon.tooltip_content" />
                                        </v-tooltip>
                                        <span v-else
                                            :key="icon_key"
                                            :class="{
                                                'table__alert--map-active': is_monitor_open_emap,
                                                'raw-html--center': icon.center
                                            }"
                                            @click="focus_on_sign(item, value)"
                                            v-html="icon.html"
                                        />
                                    </template>
                                </template>
                                <span v-else>
                                    {{ item[value.value] }}
                                </span>
                            </td> 
                        </tr>
                    </template>
                </v-data-table>
                <div v-show="show_copyright_logo" 
                    class="copyright"
                >
                    <img 
                        src="/static/images/capture/blue_cwa_logo_string_eng.png"
                        class="copyright__img"
                    >
                </div>
                <div class="diagram-dialog" 
                    :style="css_dialog_props"
                >
                    <slot 
                        name="diagram_dialog"
                        :close_dialog="close_dialog"
                    />
                </div>
            </div>
            <div 
                class="table__btn-container"
            >
                <v-btn 
                    v-if="show_description_dialog_btn"
                    class="table__btn"
                    @click="open_description_dialog"
                    fab
                    x-small
                >
                    <v-icon>icon-help</v-icon>
                    <span class="table__btn-text">{{ $t('__datatable.hint') }}</span>
                </v-btn>
                <v-btn 
                    v-if="show_download_doc_btn"
                    class="table__btn"
                    @click="call_monitor_document_ajax"
                    fab
                    x-small
                >
                    <v-icon>icon-doc-text</v-icon>
                    <span class="table__btn-text">附錄文件</span>
                </v-btn>
                <v-btn 
                    v-if="show_download_pdf_btn"
                    class="table__btn"
                    @click="download_as_pdf"
                    fab
                    x-small
                >
                    <v-icon color="#2366f8">
                        icon-file-download
                    </v-icon>
                    <span class="table__btn-text">下載表格</span>
                </v-btn>
                <v-btn 
                    v-if="show_download_png_btn"
                    class="table__btn"
                    @click="download_as_png"
                    fab
                    x-small
                >
                    <v-icon color="#2366f8">
                        icon-file-download
                    </v-icon>
                    <span class="table__btn-text">下載圖片</span>
                </v-btn>
            </div>
            <v-dialog
                v-model="description_dialog"
                height="100%"
                content-class="table-dialog"
            >
                <div class="table-description">
                    <div class="table-description__header">
                        <v-icon color="#ffffff">
                            icon-help
                        </v-icon>
                        說明
                        <v-btn 
                            class="table-description__close-btn" 
                            color="#ffffff"
                            @click="description_dialog=false" 
                            icon
                        >
                            <v-icon>icon-close</v-icon>
                        </v-btn>
                    </div>
                    <div class="table-description__content">
                        <slot 
                            name="table_description"
                        >
                            <div>
                                <span>預警: <v-icon class="signal--warning">icon-pentagon</v-icon></span>
                                <span>警戒: <v-icon class="signal--alert">icon-square</v-icon></span>
                                <span>行動: <v-icon class="signal--action">icon-triangle</v-icon></span>
                            </div>
                            <div>
                                說明:<br>
                                1. 「--」 表示該欄位無資料。<br>
                                2.雨量資料問題請撥打(02)2349-7972。<br>
                            </div>
                        </slot>
                    </div>
                </div>
            </v-dialog>
        </template>
        <div v-else-if="error_text">
            <v-alert
                :value="true"
                type="info"
                class="alert"
            >
                {{ error_text }}
            </v-alert>
        </div>
        <div v-else>
            <v-alert
                :value="true"
                class="info color_white"
                color="#3366a9"
            >
                <v-progress-circular
                    indeterminate
                    color="black"
                    class="color_white"
                />
                &nbsp;&nbsp;&nbsp;  {{ $t('__datatable.loading_text') }}
            </v-alert>
        </div>
    </section>
</template>

<script>
import { monitor_document_ajax } from '@/utils/monitor/monitor.js'
import { EventBus } from '../../event-bus.js'
import html2pdf from 'html2pdf.js'
import { mapState, mapMutations } from 'vuex'
import { TABLE_VALUE_TEXT_MAPPING } from '../../config/monitor_config'

export default {
    name: 'QpeDatatable',

    props: {
        // 表格資料，型式為 array 中，包多個一樣 key 的 object
        data: {
            type: Array,
            default: () => [],
        },
        // 資料表標題陣列， 內部的格式為 {'text': name, 'value': name, 'order': i} 表格資料會跟隨 header
        headers: {
            type: Array,
            default: () => [],
        },
        // 是否顯示資料時間的選擇區塊，預設顯示，目前在串接上還沒有功能
        // 分鐘目前僅能選擇 10分 20分 ... 考慮到資料十分鐘一筆
        show_datetime: {
            type: Boolean,
            default: true,
        },
        // 是否顯示搜尋列，預設顯示
        show_search_bar: {
            type: Boolean,
            default: true,
        },
        show_header: {
            type: Boolean,
            default: true,
        },
        show_footer: {
            type: Boolean,
            default: false,
        },
        // 設定表格標題的背景顏色
        header_background_color: {
            type: String,
            default: '#e8ecf7',
        },
        // 設定表格標題的文字顏色
        header_text_color: {
            type: String,
            default: '#121624',
        },
        // 表格內容的，必須指定表格頁面父元件的id，才能修改傳入的顏色，用意為避免不同的表格 css 互相影響
        table_page_component_id: {
            type: String,
            default: 'monitor-table-page',
        },
        // 傳入當前使用 table 的上層 component 中文，用作下載文件 api 的參數
        name: {
            type: Number,
            require: true,
            default: -1,
        },
        // 如果有傳入，顯示錯誤訊息
        error_text: {
            type: String,
            default: '',
        },

        // 開啟點擊表格展開圖表的功能，要再上層搭配 v-slot
        enable_diagram_slot: {
            type: Boolean,
            default: false,
        },
        // 指定哪些欄位可以展開圖表
        should_draw_diagram_header: {
            type: Array,
            default: () => [],
        },
        // 指定哪些欄位滑鼠覆蓋時會顯示 tooltip
        should_display_info_header_key_value: {
            type: Object,
            default: () => ({}),
        },
        // 顯示欄位 tooltip 的邏輯 function
        generate_tooltop_display_info: {
            type: Function,
            default: () => {
                return () => {}
            },
        },
        // 讓表格可以根據傳入的規則變色，目前是只有 style 設置的 css 才有
        enable_table_label: {
            type: Boolean,
            default: false,
        },
        // 傳入表格變色的邏輯 function
        table_label_function: {
            type: Function,
            default: () => {
                return () => {}
            },
        },
        show_description_dialog_btn: {
            type: Boolean,
            default: true,
        },
        // 是否要顯示下載文件的按鈕
        show_download_doc_btn: {
            type: Boolean,
            default: false,
        },
        show_download_pdf_btn: {
            type: Boolean,
            default: false,
        },
        show_download_png_btn: {
            type: Boolean,
            default: false,
        },
        table_text_for_download_pdf: {
            type: String,
            default: '',
        },
        should_show_raw_html_header: {
            type: Array,
            default: () => [],
        },
        custom_header: {
            type: Boolean,
            default: false,
        },
        no_data_text: {
            type: String,
            default: '目前無資料',
        },
        search: {
            type: String,
            default: '',
        },
        show_table_border: {
            type: Boolean,
            default: false,
        },
        props_items_per_page: {
            type: Number,
            default: 100,
        },

        is_show_chart_dialog_parent: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            table_data_structure: [],

            all_time_spans: ['10分鐘', '1小時', '3小時', '6小時', '12小時', '24小時', '48小時', '72小時'],
            footer_props: {
                showFirstLastPage: true,
                firstIcon: 'icon-keyboard-arrow-backward',
                lastIcon: 'icon-keyboard-arrow-forward',
                'items-per-page-text': '',
                'show-current-page': true,
                'items-per-page-options': [100, 200, 300, -1],
            },
            diagram_dialog: false,
            history_data: {},
            time_span: '',
            station_code: '',
            station_name: '',
            table_width: '',

            items_per_page: this.show_footer ? this.props_items_per_page : -1,
            show_copyright_logo: false,

            description_dialog: false,
        }
    },

    computed: {
        ...mapState(['display_block_width']),

        ...mapState('monitor', ['monitor_type']),

        ...mapState('emap-monitor', ['is_monitor_open_emap', 'focal_sign_number']),

        css_props() {
            return {
                '--th-text-color': this.header_text_color,
                '--th-background-color': this.header_background_color,
            }
        },

        css_dialog_props() {
            return {
                bottom: this.is_show_chart_dialog ? '0' : '-100%',
                width: `${this.display_block_width}px`,
            }
        },

        should_display_info_header() {
            return Object.keys(this.should_display_info_header_key_value)
        },

        trigger_table_data_structure_flag() {
            return [...this.headers, ...this.data]
        },

        is_show_chart_dialog: {
            get() {
                return this.is_show_chart_dialog_parent
            },

            set(new_boolean) {
                this.$emit('update:is_show_chart_dialog_parent', new_boolean)
            },
        },
    },

    watch: {
        trigger_table_data_structure_flag: {
            immediate: true,
            handler() {
                if (!this.headers.length) return (this.table_data_structure = [])

                this.table_data_structure = [
                    {
                        headers: this.headers,
                        data: this.data,
                    },
                ]
            },
        },

        focal_sign_number() {
            const sorted_data = [...this.data].sort((current) => {
                const current_item_number = current['項次'] || current['編號']
                return current_item_number === this.focal_sign_number ? -1 : 0
            })
            this.table_data_structure = [
                {
                    headers: this.headers,
                    data: sorted_data,
                },
            ]
        },

        data() {
            this.$nextTick(() => {
                const table = document.querySelector('#monitor-datatable')
                const column_max_width = table.clientWidth * 0.4
                const table_th_column = document.querySelectorAll('.v-data-table thead tr th')
                table_th_column.forEach((column) => {
                    // if (column.clientWidth > column_max_width) column.style.width = column_max_width + 'px'
                })
            })
        },
    },

    created() {
        this.init_header_color()
        EventBus.$on('move_to_table_row', this.move_to_table_row)
    },

    destroyed() {
        EventBus.$off('move_to_table_row', this.move_to_table_row)
    },

    methods: {
        ...mapMutations('emap-monitor', ['set_focal_sign_number']),

        init_header_color() {
            // 同一頁有兩張表時不 setTimeout 會有問題
            setTimeout(() => {
                let th_selector = document.querySelectorAll(`#${this.table_page_component_id} th`)
                th_selector.forEach((item) => {
                    item.style.setProperty('color', this.header_text_color, 'important')
                    item.style.setProperty('background', this.header_background_color, 'important')
                })
            }, 0)
        },

        is_style_align_center(item, column = '') {
            const reg = /^[-+]?\d+[-+]?(\.\d+[-+*⁺]?)?$|^[-+]{1,2}[*⁺]?$|^\d{1,2}:\d{1,2}$/
            const specil_columns = [
                '站號',
                '參考浮標站',
                '雨量站代碼',
                '警示',
                '閃電警示',
                'QPE警示',
                '對流胞警示',
                '閃電警示類型',
                '10分前',
                '20分前',
                '30分前',
                '40分前',
                '50分前',
            ] // 特殊欄位判斷
            // 以下為合格的範例: "-0.0, +0.0, 0.0, 0, --, 0.0-, 0.0+, 00:00" 純數字(前後有-(+)，有小數點都為合格)，-- (目前無數值)，時間；特殊案例，瞬間風特殊符號"*⁺"放在數字後或是 -- 後

            return reg.test(item) || specil_columns.includes(column)
        },

        show_dialog(header_name, table_value, item) {
            this.$emit('open-chart', header_name, table_value, item)
            this.is_show_chart_dialog = true
        },

        close_dialog() {
            this.is_show_chart_dialog = false
        },

        call_monitor_document_ajax() {
            monitor_document_ajax.call(this, this.monitor_type)
        },

        move_to_table_row(item) {
            if (this.data.filter((item) => item['警示']).length > this.items_per_page) {
                ;(this.items_per_page = -1),
                    setTimeout(() => {
                        let objective_dom = document.querySelector(`#row_${item.idx}`)
                        window.scroll(0, objective_dom.offsetTop + 80)
                    }, 0)
            } else {
                const signs_dom = document.querySelector('#monitor-signs')
                const objective_dom = document.querySelector(`#row_${item.idx}`)
                window.scroll(0, signs_dom.scrollHeight + objective_dom.offsetTop)
            }
        },

        custom_sort(items, indices, is_desc) {
            if (indices === null || !indices.length) return items

            const is_value_valid = (value) => {
                return !['None', 'none', 'NaN', 'nan', '-', '--', '--(None)'].includes(value)
            }

            const sort_by_desc_or_asc_number = (a, b) => {
                if (!is_desc[0]) {
                    return a - b
                } else {
                    return b - a
                }
            }

            const sort_by_desc_or_asc_string = (a, b) => {
                if (!is_desc[0]) {
                    return a.toLowerCase().localeCompare(b.toLowerCase())
                } else {
                    return b.toLowerCase().localeCompare(a.toLowerCase())
                }
            }

            const index = indices[0]
            const no_data_items = items.filter((item) => !is_value_valid(item[index]))

            const GUST_KEY = ['Wind Gust (m/s)', '瞬間風(m/s)']
            const is_sort_gust = GUST_KEY.includes(index)
            const has_data_items = items.filter((item) => is_value_valid(item[index])).sort((a, b) => {
                const float_a_value = is_sort_gust ? Number(a[index].replaceAll(/⁺|\*/g, '')) : Number(a[index])
                const float_b_value = is_sort_gust ? Number(b[index].replaceAll(/⁺|\*/g, '')) : Number(b[index])
                const is_both_float = !isNaN(float_a_value) && !isNaN(float_b_value)
                if (is_both_float) {
                    return sort_by_desc_or_asc_number(float_a_value, float_b_value)
                } else if (typeof a[index] === 'object') {
                    const stringify_a_value = JSON.stringify(a[index])
                    const stringify_b_value = JSON.stringify(b[index])
                    return sort_by_desc_or_asc_string(stringify_a_value, stringify_b_value)
                } else {
                    return sort_by_desc_or_asc_string(a[index], b[index])
                }
            })
            return [...has_data_items, ...no_data_items]
        },

        change_width() {
            this.$nextTick(() => {
                const table = document.querySelector('#monitor-datatable')
                const column_max_width = table.clientWidth * 0.4
                const table_th_column = document.querySelectorAll('.v-data-table thead tr th')
                table_th_column.forEach((column) => {
                    if (column.clientWidth > column_max_width) column.style.width = column_max_width + 'px'
                })
            })
        },

        download_as_pdf() {
            this.show_copyright_logo = true
            const immutable_items_per_page = this.items_per_page
            const immutable_table_structure = [...this.table_data_structure]
            const DATA_COUNT_PER_PAGE = 10

            this.items_per_page = DATA_COUNT_PER_PAGE
            this.table_data_structure = this.table_data_structure[0].data.reduce((accumulator, datum, index) => {
                const page_num = Math.floor(index / DATA_COUNT_PER_PAGE)
                const is_next_table = index % DATA_COUNT_PER_PAGE === 0

                if (is_next_table) {
                    return [
                        ...accumulator,
                        {
                            headers: this.table_data_structure[0].headers,
                            data: [datum],
                        },
                    ]
                } else {
                    accumulator[page_num].data = [...accumulator[page_num].data, datum]

                    return accumulator
                }
            }, [])

            const table_text =
                this.table_text_for_download_pdf ?? TABLE_VALUE_TEXT_MAPPING[this.name]['tag_name'] ?? '監控表格資料'

            const options = {
                margin: 5,
                filename: `${table_text}.pdf`,
                jsPDF: { orientation: 'l' },
            }

            this.$nextTick(() => {
                const table_element = document.querySelectorAll('.table__content')
                let download_worker = html2pdf()
                    .set(options)
                    .from(table_element[0])
                    .toPdf()

                // download_worker
                ;[...table_element].slice(1).forEach((row) => {
                    download_worker = download_worker
                        .get('pdf')
                        .then((pdf) => pdf.addPage())
                        .from(row)
                        .toContainer()
                        .toCanvas()
                        .toPdf()
                })
                download_worker.save().then(() => {
                    this.table_data_structure = immutable_table_structure
                    this.items_per_page = immutable_items_per_page
                    this.show_copyright_logo = false
                })
            })
        },

        download_as_png() {
            //舊版: 只截表格
            const element = document.getElementsByClassName('v-data-table__wrapper')[0]
            if (element) {
                element.scrollTop = 0
                element.scrollLeft = 0
                const w = element.childNodes[0].clientWidth
                const h = element.childNodes[0].clientHeight
                html2canvas(element, {
                    width: w,
                    height: h,
                    windowWidth: w,
                    windowHeight: h,
                    scale: 1,
                    useCORS: true,
                    removeContainer: true,
                    scrollX: 0,
                    scrollY: 0,
                    onclone(doc) {
                        const style = doc.createElement('style')
                        style.innerHTML = `
        *{animation:none!important;transition:none!important}
        .no-shot{visibility:hidden!important}
      `
                        doc.head.appendChild(style)
                    },
                    ignoreElements: (el) => el.classList?.contains('no-shot'),
                }).then((canvas) => {
                    const imgData = canvas.toDataURL('image/png')
                    const link = document.createElement('a')
                    link.href = imgData
                    link.download = `${new Date()}.png`
                    link.click()
                })
            }
        },
        async download_as_png() {
            const resetCursor = this.setLoadingCursorAndLock()
            try {
                // 先把表格滾到上面（避免截到中段）
                const tableWrapper = document.querySelector('.v-data-table__wrapper')
                if (tableWrapper) {
                    tableWrapper.scrollTop = 0
                    tableWrapper.scrollLeft = 0
                }

                // 讓 DOM 更新 + layout 穩定（很重要）
                await this.$nextTick?.()
                await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

                const selectors = [
                    '.monitor-container header.logo__container', // Logo
                    '.monitor-table-page__title', // 標題
                    '.datetime', // 日期時間
                    '.v-data-table__wrapper table', // table
                ]

                const baseH2cOption = {
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: '#ffffff',
                    scrollX: 0,
                    scrollY: 0,
                    removeContainer: true,
                    onclone(doc) {
                        const style = doc.createElement('style')
                        style.innerHTML = `
        * { animation: none !important; transition: none !important; }
        .no-shot { visibility: hidden !important; }
      `
                        doc.head.appendChild(style)

                        // 保險：避免 clone 後 wrapper 還保留滾動狀態
                        const w = doc.querySelector('.v-data-table__wrapper')
                        if (w) {
                            w.scrollTop = 0
                            w.scrollLeft = 0
                        }
                    },
                }

                const getEl = (sel) => document.querySelector(sel)

                const getRect = (el) => {
                    // getBoundingClientRect 有時會是小數，h2c 可能裁切；取 ceil 更穩
                    const r = el.getBoundingClientRect()
                    return {
                        width: Math.ceil(r.width),
                        height: Math.ceil(r.height),
                        left: Math.floor(r.left),
                        top: Math.floor(r.top),
                    }
                }

                const captureOne = async (sel) => {
                    const el = getEl(sel)
                    if (!el) {
                        console.warn('找不到元素：', sel)
                        return null
                    }

                    const { width, height } = getRect(el)
                    if (!width || !height) {
                        console.warn('元素寬高為 0，略過：', sel, { width, height })
                        return null
                    }

                    // 針對該元素帶入 windowWidth/windowHeight，避免裁切/0x0
                    const opt = {
                        ...baseH2cOption,
                        width,
                        height,
                        windowWidth: document.documentElement.clientWidth,
                        windowHeight: document.documentElement.clientHeight,
                    }

                    try {
                        const canvas = await html2canvas(el, opt)
                        if (!canvas?.width || !canvas?.height) {
                            console.warn('html2canvas 產生 0x0 canvas，略過：', sel)
                            return null
                        }
                        return { selector: sel, canvas }
                    } catch (err) {
                        console.error('截圖失敗：', sel, err)
                        return null
                    }
                }

                // 逐一截圖（保留順序；你也可以改 Promise.all 平行，但較吃記憶體）
                const canvasList = []
                for (const sel of selectors) {
                    const result = await captureOne(sel)
                    if (result) canvasList.push(result)
                }

                if (!canvasList.length) {
                    console.error('沒有任何有效的 canvas 被產生')
                    return
                }

                // 合併 canvas
                const padding = 20
                const totalHeight = canvasList.reduce((sum, o) => sum + o.canvas.height, 0)
                const canvasHeight = totalHeight + padding * 2
                const maxWidth = Math.max(...canvasList.map((o) => o.canvas.width))
                const canvasWidth = maxWidth + padding * 2

                const finalCanvas = document.createElement('canvas')
                finalCanvas.width = canvasWidth
                finalCanvas.height = canvasHeight

                const ctx = finalCanvas.getContext('2d')
                ctx.fillStyle = '#ffffff'
                ctx.fillRect(0, 0, canvasWidth, canvasHeight)

                let offsetY = padding
                for (const { selector, canvas } of canvasList) {
                    ctx.drawImage(canvas, padding, offsetY)
                    offsetY += canvas.height
                }

                // 輸出
                finalCanvas.toBlob((blob) => {
                    if (!blob) return
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `monitor-table-${Date.now()}.png`
                    link.click()
                    URL.revokeObjectURL(url)
                }, 'image/png')
            } finally {
                resetCursor()
            }
        },
        setLoadingCursorAndLock() {
            const prevCursor = document.documentElement.style.cursor
            const prevPE = document.body.style.pointerEvents

            document.documentElement.style.cursor = 'wait'
            document.body.style.pointerEvents = 'none'

            return () => {
                document.documentElement.style.cursor = prevCursor
                document.body.style.pointerEvents = prevPE || ''
            }
        },
        open_description_dialog() {
            this.description_dialog = true
        },

        get_signal_focal_class(item, { text }) {
            if (text !== '警示') return ''

            return (item['警示'] && item['警示'][0]?.td_class) || ''
        },

        focus_on_sign(item, value) {
            if (value.value !== '警示') return
            this.set_focal_sign_number(item['項次'] || item['編號'] || '')
        },
    },
}
</script>

<style lang="scss">
.monitor-datatable {
    position: relative;
    padding: 0 1rem;
    width: 100%;
}

.signal {
    // 五邊形
    &--warning.signal--warning {
        color: var(--base-yellow);
    }

    // 菱形，為了覆蓋 v-icon 的動畫，多寫一次 selector
    &--alert.signal--alert.signal--alert {
        transform: scale(0.75) rotate(45deg);
        color: var(--base-orange);
        transition: none;
    }

    // 三角形
    &--action.signal--action.signal--action {
        transform: scale(0.9) translateX(-2.5%);
        color: var(--base-red);
        transition: none;
    }
}

.table {
    &__content {
        position: relative;

        .v-data-table.bg-opacity-white {
            background-color: rgba(255, 255, 255, 0.5);
        }

        .border_right,
        .border_right_last:nth-child(3n) {
            border-right: 1px solid rgba(255, 255, 255, 0.12);
        }

        th {
            padding: 2px 1px !important;
            font-size: 15px;
            font-weight: bold !important;
            // white-space: nowrap;
            color: var(--th-text-color) !important;
            background-color: var(--th-background-color) !important;
        }

        .tr {
            &--focal {
                box-shadow: 1px 2px var(--white-border);
            }
        }

        td {
            padding: 2px 1px !important;

            &:not(:last-child) {
                border-right: 1px solid var(--white-border);
            }
        }

        .td {
            &--focal {
                border-right: 1px solid #aeaeae !important;
                box-shadow: -1px 1px 1px 0 #aeaeae;
            }

            &__signal {
                // 五邊形
                &--warning {
                    background: rgba(241, 217, 74, 0.24);
                }

                // 菱形
                &--alert {
                    background: rgba(237, 161, 47, 0.24);
                }

                // 三角形
                &--action {
                    background: rgba(238, 57, 52, 0.24);
                }
            }
        }

        .v-data-table-header th:not(:last-child) {
            border-right: 1px solid #fff;
        }

        .icon__image {
            max-width: 30px;
            max-height: 30px;
            vertical-align: middle;

            &--inactive {
                opacity: 0.3;
            }
        }

        .v-data-table-header__icon {
            display: none !important;
            font-size: 0.75rem !important;
        }

        .active .v-data-table-header__icon {
            display: inline-flex !important;
        }

        .dot {
            display: inline-block;
            width: 1.5em;
            height: 1.5em;
            border-radius: 50%;
        }

        .signal-signs {
            display: inline-block;
            width: 22px;
            font-weight: bold;
            text-align: center;
            border-radius: 50%;
        }

        .text_align_center {
            text-align: center;
        }

        .links {
            color: var(--base-blue);

            &:hover {
                cursor: pointer;
            }
        }

        .vertical-center {
            top: 50%;
            transform: translateY(-50%);
        }

        .tooltip__box {
            display: inline-block;
            min-width: 2rem;
            min-height: 2vh;
        }

        .v-tooltip__content {
            white-space: pre;
        }

        .alert {
            height: 50px;
        }

        .color_white {
            color: #fff !important;
        }

        .raw-html--center {
            display: flex;
            justify-content: center;
        }
    }

    &__btn {
        margin: 0 0.4rem;

        &-container {
            position: fixed;
            bottom: 1rem;
            left: 1rem;
            background: rgba(255, 255, 255, 0.5);
        }

        &-text {
            position: absolute;
            transform: translateY(-2rem);
            display: none;
            background: #fff;
        }

        &:hover .table__btn-text {
            text-transform: capitalize;
            display: block;
        }
    }

    &-dialog {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        max-width: 90%;
        box-shadow: 0 0 0 0;
    }
}

.diagram-dialog {
    position: fixed;
    bottom: -100%;
    left: 0;
    z-index: 5;
    transition: bottom 0.8s ease-out;
}
</style>

<style lang="scss" scoped>
.table {
    &__alert {
        &--map-active {
            cursor: pointer;
        }
    }
}

.table-description {
    width: 684px;
    min-height: 484px;
    font-weight: bold;

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 56px;
        font-size: 20px;
        line-height: 1.5;
        color: #fff;
        background: var(--base-dark-blue);
        border-radius: 1rem 1rem 0 0;
    }

    &__close-btn {
        position: absolute;
        right: 1rem;
    }

    &__content {
        padding: 1rem;
        font-size: 14px;
        letter-spacing: 0.6px;
        line-height: 1.43;
        color: var(--base-dark-blue);
        background: #fff;
        border: 1rem solid var(--white-border);
        border-radius: 0 0 1rem 1rem;
    }
}

.capture {
    position: fixed;
    top: 50%;
    left: 40%;
    transform: translate(-50%, -50%);
    padding: 0.3rem 0.2rem 0.3rem 0.3rem;
    line-height: 40px;
    text-align: center;
    background-image: linear-gradient(to right, #2366f8 0%, #2cfde1 100%);
    border-radius: 0.5rem;
    z-index: 1;
    box-shadow: 0 0 5px #888;

    &__text-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 1rem;
        font-size: 14px;
        background-color: #fff;
        border-radius: 0.5rem;
    }

    &__progress {
        margin-right: 0.5rem;
    }
}

.copyright {
    position: absolute;
    top: 10rem;
    right: 9rem;
    display: flex;
    flex-direction: column;
    z-index: -1;

    &__img {
        width: 10rem;
    }
}
</style>

<style>
@import url('../../../css/custom_qpe_datatable.css');
</style>
